// First-party, privacy-safe analytics client. No PII is ever sent.
// Honors Do-Not-Track / Global Privacy Control. Stores only random ids + UTM.
import api from "./api";

const VID = "db_vid";
const VID_FIRST = "db_vid_first_done";
const SID = "db_sid";
const SID_TS = "db_sid_ts";
const ATTR_FIRST = "db_attr_first";
const ATTR_LATEST = "db_attr_latest";
const REFERRAL = "db_referral";
const SESSION_WINDOW_MS = 30 * 60 * 1000;

function uuid() {
  try {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  } catch (e) { /* noop */ }
  return "v-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function trackingDisabled() {
  try {
    const dnt = navigator.doNotTrack || window.doNotTrack || navigator.msDoNotTrack;
    return dnt === "1" || dnt === "yes" || navigator.globalPrivacyControl === true;
  } catch (e) { return false; }
}

function getVisitorId() {
  let id = localStorage.getItem(VID);
  if (!id) { id = uuid(); localStorage.setItem(VID, id); }
  return id;
}

function consumeIsNewVisitor() {
  // true only for the very first pageview after a visitor id is created
  if (!localStorage.getItem(VID_FIRST)) {
    localStorage.setItem(VID_FIRST, "1");
    return true;
  }
  return false;
}

function getSessionId() {
  const now = Date.now();
  const last = parseInt(sessionStorage.getItem(SID_TS) || localStorage.getItem(SID_TS) || "0", 10);
  let id = sessionStorage.getItem(SID) || localStorage.getItem(SID);
  if (!id || !last || now - last > SESSION_WINDOW_MS) {
    id = uuid();
    sessionStorage.setItem(SID, id);
    localStorage.setItem(SID, id);
  }
  sessionStorage.setItem(SID_TS, String(now));
  localStorage.setItem(SID_TS, String(now));
  return id;
}

function readJson(key) {
  try { return JSON.parse(localStorage.getItem(key) || "null"); } catch (e) { return null; }
}

// Capture UTM + ref from the current URL. First-touch is never overwritten.
export function captureAttribution() {
  try {
    const q = new URLSearchParams(window.location.search);
    const utm = {
      source: q.get("utm_source") || undefined,
      medium: q.get("utm_medium") || undefined,
      campaign: q.get("utm_campaign") || undefined,
      content: q.get("utm_content") || undefined,
    };
    const hasUtm = Object.values(utm).some(Boolean);
    if (hasUtm) {
      if (!readJson(ATTR_FIRST)) localStorage.setItem(ATTR_FIRST, JSON.stringify(utm));
      localStorage.setItem(ATTR_LATEST, JSON.stringify(utm));
    }
    const ref = q.get("ref") || q.get("referral");
    if (ref && !localStorage.getItem(REFERRAL)) localStorage.setItem(REFERRAL, ref.slice(0, 128));
  } catch (e) { /* noop */ }
}

function latestUtm() {
  return readJson(ATTR_LATEST) || readJson(ATTR_FIRST) || undefined;
}

let _lastPv = { path: null, ts: 0 };

function _isDuplicatePageview(p) {
  const now = Date.now();
  try {
    const raw = sessionStorage.getItem("db_last_pv");
    if (raw) {
      const o = JSON.parse(raw);
      if (o && o.p === p && now - o.t < 1500) return true;
    }
    sessionStorage.setItem("db_last_pv", JSON.stringify({ p, t: now }));
    return false;
  } catch (e) {
    if (_lastPv.path === p && now - _lastPv.ts < 1500) return true;
    _lastPv = { path: p, ts: now };
    return false;
  }
}

export async function trackPageview(path) {
  if (trackingDisabled()) return;
  const p = path || window.location.pathname;
  // Guard against StrictMode / re-render / fast-refresh double-fires (persists across hard reload in-tab).
  if (_isDuplicatePageview(p)) return;
  try {
    await api.post("/growth/collect", {
      kind: "pageview",
      path: p,
      referrer: document.referrer || "",
      visitor_id: getVisitorId(),
      session_id: getSessionId(),
      is_new_visitor: consumeIsNewVisitor(),
      utm: latestUtm(),
    });
  } catch (e) { /* analytics must never break the app */ }
}

export async function trackEvent(name, path) {
  if (trackingDisabled()) return;
  try {
    await api.post("/growth/collect", {
      kind: "event",
      name,
      path: path || window.location.pathname,
      referrer: document.referrer || "",
      visitor_id: getVisitorId(),
      session_id: getSessionId(),
      utm: latestUtm(),
    });
  } catch (e) { /* noop */ }
}

// Stamp first-touch + latest attribution and referral onto the authed user record.
export async function sendAttribution() {
  try {
    const first = readJson(ATTR_FIRST);
    const latest = readJson(ATTR_LATEST);
    const referral = localStorage.getItem(REFERRAL) || undefined;
    if (!first && !latest && !referral) return;
    await api.post("/growth/attribution", { first: first || undefined, latest: latest || undefined, referral });
  } catch (e) { /* noop */ }
}
